import { createPortal } from 'react-dom';
import { useState } from 'react';

import { Dialog, DialogClose, DialogContent, DialogContentInner, DialogOverlay } from '../Dialog';
import { CompoundPricing } from './Pricing.types';
import { Icon } from '../../../../text/Icon/Icon';
import { Button } from '../../../../controls/Button/Button';
import { PriceEntry } from './PriceEntry';
import { styled } from '../../../../../stitches.config';
import { PricingList } from './PricingList';
import { SM_PROVIDER_META, StartingMaterialDatasetProvider } from './suppliers';
import { Compound } from '../../../Compound/Compound';

const PricingContainer = styled('div', {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '$7',
  padding: '0 $7',
});

const PricingListContainer = styled('ul', {
  listStyle: 'none',
  padding: '$8',
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '$7',
});

const PricingItem = styled('li', {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '$6',
});

const PricingContent = styled('div', {
  flex: 1,
});

const PricingItemHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$6',
  marginBottom: '$3',
});

const PriceEntries = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$2',
});

const DatasetName = styled('h3', {
  lineHeight: 1,
  margin: '0',
  fontSize: '$5',
});

const DatasetCreatedAt = styled('span', {
  color: '$slate11',
  fontSize: '$1',
});

const CompoundPreview = styled(Compound, {
  position: 'sticky',
  top: 0,
  width: '15rem',
  pointerEvents: 'none',
  padding: '$4',
  backgroundColor: 'white',
  borderRadius: '$small',
  border: '1px solid $slate5',
});

interface PricingProps {
  compoundPricing: CompoundPricing[] | null;
  close: () => void;
}
export function Pricing(props: PricingProps) {
  const { compoundPricing, close } = props;

  const smiles = compoundPricing?.[0]?.entries[0]?.originalSmiles;

  const isOpen = compoundPricing != null;

  const [viewMap, setViewMap] = useState<Record<string, boolean>>({});

  const toggleView = (datasetId: string) => () => {
    setViewMap({
      ...viewMap,
      [datasetId]: !viewMap[datasetId],
    });
  };

  return createPortal(
    <Dialog open={isOpen}>
      <DialogOverlay onClick={close}>
        <DialogContent onClick={e => e.stopPropagation()}>
          <DialogContentInner>
            {compoundPricing && (
              <PricingContainer>
                <CompoundPreview smiles={smiles ?? ''} />
                <PricingListContainer>
                  {compoundPricing.map(dataset => {
                    const provider = dataset.provider as StartingMaterialDatasetProvider;
                    const isSinglePrice = dataset.entries.length === 1;
                    const isProviderCas = provider === 'cas';

                    return (
                      <PricingItem key={dataset.datasetName}>
                        <Button
                          weight="outlined"
                          theme="purple"
                          icon={viewMap[dataset.datasetId] ? 'keyboard_arrow_down' : 'chevron_right'}
                          onClick={toggleView(dataset.datasetId)}
                          disabled={isProviderCas || dataset.entries.length < 1}
                        />
                        <PricingContent>
                          <PricingItemHeader>
                            <div>
                              <DatasetName>{dataset.datasetName}</DatasetName>
                              {dataset.createdAt && (
                                <DatasetCreatedAt>
                                  Current as of {new Date(dataset.createdAt).toLocaleDateString()}
                                </DatasetCreatedAt>
                              )}
                            </div>
                            {provider && (
                              <Button
                                weight="outlined"
                                theme="purple"
                                text={`Find more in ${SM_PROVIDER_META[provider].name}`}
                                rightIcon="exit_to_app"
                                size="small"
                                onClick={() => {
                                  window.open(
                                    SM_PROVIDER_META[provider].generateLink(dataset.cheapestEntryOverall),
                                    '_blank'
                                  );
                                }}
                              />
                            )}
                          </PricingItemHeader>
                          <div>
                            {!viewMap[dataset.datasetId] && (
                              <div>
                                {!isSinglePrice && (
                                  <PriceEntries>
                                    <PriceEntry entry={dataset.cheapestEntryOverall} title="Cheapest" icon="savings" />
                                    <PriceEntry
                                      entry={dataset.cheapestEntryInLowestTier}
                                      title="Shortest shipping time"
                                      icon="bolt"
                                    />
                                  </PriceEntries>
                                )}

                                {isSinglePrice && !isProviderCas && (
                                  <PriceEntry
                                    entry={dataset.cheapestEntryOverall}
                                    title="Single price"
                                    icon="shopping_cart"
                                  />
                                )}

                                {isSinglePrice && isProviderCas && (
                                  <PriceEntry entry={dataset.cheapestEntryOverall} title="Cheapest" icon="savings" />
                                )}
                              </div>
                            )}
                            {viewMap[dataset.datasetId] && (
                              <>
                                <PricingList entries={dataset.entries} />
                                {dataset.entries.length > 20 && (
                                  <Button
                                    weight="outlined"
                                    theme="purple"
                                    text="Hide prices"
                                    icon="keyboard_arrow_up"
                                    onClick={toggleView(dataset.datasetId)}
                                  />
                                )}
                              </>
                            )}
                          </div>
                        </PricingContent>
                      </PricingItem>
                    );
                  })}
                </PricingListContainer>
              </PricingContainer>
            )}

            <DialogClose aria-label="Close" onClick={close}>
              <Icon icon="close" />
            </DialogClose>
          </DialogContentInner>
        </DialogContent>
      </DialogOverlay>
    </Dialog>,
    document.getElementById('widget-container') ?? document.body
  );
}
